"use client";
import { makeStyles } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import ImageForm, { ImageFormData } from "../components/image/ImageForm";
import { chapterImageSize } from "../constants/ImageConstants";
import { useCreateChapterImage, useCreateDraft } from "../hooks/useImages";
import { ClientRoutes } from "../models/ClientRoutes";
import { CreateImageRequest } from "../models/Image";
import { ImageUploadType } from "../services/ImageService";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
  },
  title: {
    fontSize: "32px",
    fontWeight: "600",
    margin: "0 0 24px 0",
    textAlign: "center",
  },
});

const CreateImagePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const classes = useStyles();

  const { mutateAsync: createChapterImage, isPending: chapterPending } =
    useCreateChapterImage();
  const { mutateAsync: createDraftImage, isPending: draftPending } =
    useCreateDraft();

  const isChapterImage = location.pathname.includes("/chapter/");
  const isActivityImage = location.pathname.includes("/activity/");

  const isLoading = chapterPending || draftPending;

  const handleFormSubmit = async (formData: ImageFormData) => {
    if (!formData.file) return;

    const requestData = new FormData();
    requestData.append("Title", formData.title);
    requestData.append("FileName", formData.name);
    requestData.append("ImageFile", formData.file);

    if (isActivityImage) {
      requestData.append("ImageId", "");
    }

    try {
      if (isChapterImage) {
        const createdImage = await createChapterImage(
          requestData as unknown as CreateImageRequest
        );
        navigate(`${ClientRoutes.MediaChapterEdit}/${createdImage.imageId}`);
      } else {
        const createdImage = await createDraftImage(
          requestData as unknown as CreateImageRequest
        );
        navigate(`${ClientRoutes.MediaActivityEdit}/${createdImage.versionId}`);
      }
    } catch (error) {
      console.error("Failed to create image:", error);
    }
  };

  const handleCancel = () => {
    if (isChapterImage) {
      navigate(`${ClientRoutes.MediaChapterPage}`);
    } else {
      navigate(`${ClientRoutes.MediaActivityPage}`);
    }
  };

  return (
    <div className={classes.root}>
      <h1 className={classes.title}>
        {isChapterImage ? t("createChapterImage") : t("createActivityImage")}
      </h1>

      <ImageForm
        isLoading={isLoading}
        onSubmit={handleFormSubmit}
        onCancel={handleCancel}
        imageType={isChapterImage ? "chapter" : "activity"}
        imageHeightLimit={chapterImageSize.height}
        imageWidthLimit={chapterImageSize.width}
        acceptTypes={
          isChapterImage ? ImageUploadType.Chapter : ImageUploadType.Activity
        }
      />
    </div>
  );
};

export default CreateImagePage;
